import { useEffect, useState } from "react";
import { RefreshCw, Activity, Terminal } from "lucide-react";
import { Button } from "../components/ui/button";
import { ProvidersPill } from "./ProvidersPill";

// Bumped from package.json — kept inline so CRA bundles it without
// requiring REACT_APP_* env wiring.
export const APP_VERSION = "0.3b";

export function Header({
  lastUpdated,
  onRefresh,
  isRefreshing,
  providers,
  health,
  onOpenLogs,
}) {
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const lastUpdatedLabel = lastUpdated
    ? formatAge(lastUpdated)
    : "never";

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 border-b border-soft bg-[var(--bg)]/90 backdrop-blur-md"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 gap-4">
        {/* --- Brand ------------------------------------------------------ */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative flex items-center justify-center w-8 h-8 border border-soft bg-surface">
            <Activity
              className="text-accent-amber"
              size={16}
              strokeWidth={1.5}
            />
          </div>
          <div className="min-w-0">
            <h1
              className="text-xl sm:text-2xl font-black tracking-tighter uppercase leading-none whitespace-nowrap"
              data-testid="app-title"
            >
              OmniMatrix<span className="text-accent-amber">·</span>Core
            </h1>
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-soft mt-1 hidden sm:block">
              S&amp;P 500 · Live Aggregator
            </p>
          </div>
        </div>

        {/* --- Right controls -------------------------------------------- */}
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end">
          <div
            className="hidden md:flex flex-col items-end leading-tight"
            data-testid="clock-block"
          >
            <span className="num text-xs text-muted-soft uppercase tracking-[0.15em]">
              {clock.toISOString().slice(0, 10)}
            </span>
            <span className="num text-sm text-[var(--text-primary)] font-medium">
              {clock.toTimeString().slice(0, 8)} UTC
            </span>
          </div>

          <ProvidersPill providers={providers} health={health} />

          <Button
            data-testid="open-logs-button"
            variant="outline"
            size="sm"
            onClick={onOpenLogs}
            className="rounded-none border-soft bg-surface hover:bg-surface-raised hover:text-accent-amber gap-2 font-mono-tight uppercase text-xs tracking-widest"
          >
            <Terminal size={14} strokeWidth={1.5} />
            <span className="hidden sm:inline">Logs</span>
          </Button>

          <div
            className="hidden lg:flex flex-col items-end leading-tight"
            data-testid="last-updated-block"
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-soft">
              Updated
            </span>
            <span className="num text-xs text-[var(--text-primary)]">
              {lastUpdatedLabel}
            </span>
          </div>

          <Button
            data-testid="refresh-button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="rounded-none border-soft bg-surface hover:bg-surface-raised hover:text-accent-amber gap-2 font-mono-tight uppercase text-xs tracking-widest"
          >
            <RefreshCw
              size={14}
              strokeWidth={1.5}
              className={isRefreshing ? "omni-spin" : ""}
            />
            Refresh
          </Button>
        </div>
      </div>
    </header>
  );
}

function formatAge(ts) {
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}
